/**
 * Resolve clio-server API base URL at runtime, by host (#68 local dev; #81 prod fix).
 *
 * This is the OPERATIVE API-base selector: `environment.ts` wraps its config in
 * `withClioApiUrls()`, and Amplify builds with a bare `ng build` (angular.json has
 * `defaultConfiguration: None`), so the `--configuration=production` fileReplacement
 * to `environment.prod.ts` does NOT apply — the runtime value comes from here, not
 * from `environment.prod.ts`. Therefore the host→API mapping must be correct here.
 *
 * Mapping (mirrors isEvidencePlaygroundHost):
 *   localhost/127.0.0.1            → laptop clio-server :8000
 *   host contains "dev"            → apex-dev API (apexapi-dev.medstatix.com, VPN-only)
 *   everything else (apex-prod)    → apex-prod API (api-apex.medstatix.com)
 *
 * #81: before this fix, every non-localhost host fell through to the dev API, so the
 * apex-prod SPA (my.medstatix.com) called the VPN-only dev API → public users broken.
 */
const APEX_PROD_API = "https://api-apex.medstatix.com";
const APEX_DEV_API = "https://apexapi-dev.medstatix.com";
const LOCAL_CLIO_SERVER = "http://localhost:8000";

export function clioApiBase(): string {
    if (typeof window !== "undefined" && window.location) {
        const host = window.location.hostname.toLowerCase();
        if (host === "localhost" || host === "127.0.0.1") {
            return LOCAL_CLIO_SERVER;
        }
        // Dev/internal hosts carry "dev" (mydev.medstatix.com, apexapi-dev, *-dev.*,
        // the indiaDev Amplify branch). Customer-facing apex-prod hosts do not.
        if (host.includes("dev")) {
            return APEX_DEV_API;
        }
        // apex-prod: my.medstatix.com / my.clio.health / demo.medstatix.com, …
        return APEX_PROD_API;
    }
    // No window (SSR/build): default to prod — safer than dev for a customer build.
    return APEX_PROD_API;
}

export function withClioApiUrls<T extends Record<string, unknown>>(env: T): T & {
    uri: string;
    graph_uri: string;
    evidence_iframe_uri: string;
} {
    const base = clioApiBase();
    return {
        ...env,
        uri: base,
        graph_uri: `${base}/msx`,
        evidence_iframe_uri: `${base}/evidence/playground/`,
    };
}

/**
 * Whether the Evidence Playground menu link should be shown on this host.
 *
 * Evidence Playground is an internal/dev pilot — "VPN-only and not customer-facing"
 * (see environment.prod.ts). Show it only on dev hosts (localhost + the apex-dev
 * domains), never on a customer-facing apex-prod host. This is a RUNTIME hostname
 * check (same rationale as clioApiBase): build-time `environment.production` is
 * unreliable here — angular.json has no build.defaultConfiguration, so a bare
 * `ng build` uses the default (non-prod) environment, so the flag can't distinguish
 * apex-dev from apex-prod. Defaults to HIDDEN for any unrecognized host.
 */
export function isEvidencePlaygroundHost(): boolean {
    if (typeof window === "undefined" || !window.location) {
        return false;
    }
    const host = window.location.hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") {
        return true;
    }
    // apex-dev / internal hosts carry "dev" (apexapi-dev, *-dev.medstatix, the
    // indiaDev Amplify branch). Customer-facing apex-prod hosts do not.
    return host.includes("dev");
}
